import React, { useState, useEffect } from 'react';
import {
  useOrdenCompra,
  useRecepcionarOrdenCompra,
} from '../hooks/useOrdenesCompra';
import { X, Package, Check, AlertTriangle, FileText, Plus } from 'lucide-react';
import { obtenerFechaActualLocal } from '../utils/dateUtils';

// Función para formatear moneda con separadores de miles
const formatearMoneda = (cantidad: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cantidad);
};

interface RecepcionItem {
  detalleId: number;
  ingredienteNombre: string;
  unidadMedida: string;
  cantidadSolicitada: number;
  cantidadRecibidaAnterior: number;
  cantidadRecibida: number;
  cantidadPendiente: number;
  precioUnitario: number;
  estaCompleto: boolean;
  observaciones?: string;
  // Vencimiento del lote que se va a crear con esta recepción. Opcional: si
  // queda vacío el lote se crea sin fecha, como antes. Importa cargarlo porque
  // el consumo es FEFO y un lote sin vencimiento se va al final de la cola.
  fechaVencimiento?: string;
}

interface RecepcionData {
  fechaRecepcion: string;
  observacionesRecepcion?: string;
  items: RecepcionItem[];
  tipoRecepcion: 'completa' | 'parcial-mantener' | 'parcial-nueva-oc';
}

interface RecepcionOrdenCompraProps {
  isOpen: boolean;
  onClose: () => void;
  ordenId: string;
}

const RecepcionOrdenCompra: React.FC<RecepcionOrdenCompraProps> = ({
  isOpen,
  onClose,
  ordenId,
}) => {
  const { data: orden } = useOrdenCompra(ordenId, isOpen);
  const recepcionarMutation = useRecepcionarOrdenCompra();

  const [recepcionData, setRecepcionData] = useState<RecepcionData>({
    // El <input type="date"> sólo acepta `YYYY-MM-DD`. Si pasamos un ISO
    // completo, el input arranca vacío. Guardamos sólo la fecha y la
    // expandimos a ISO con hora local en el momento de enviar al backend.
    fechaRecepcion: obtenerFechaActualLocal(),
    observacionesRecepcion: '',
    items: [],
    tipoRecepcion: 'completa',
  });

  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Inicializar items cuando se carga la orden
  useEffect(() => {
    if (orden?.items) {
      const itemsInicializados: RecepcionItem[] = orden.items.map((item) => ({
        detalleId: parseInt(item.id),
        ingredienteNombre: item.ingrediente.nombre,
        unidadMedida: item.ingrediente.unidadMedida.abreviacion,
        cantidadSolicitada: item.cantidad,
        cantidadRecibidaAnterior: item.cantidadRecibida || 0,
        // cantidadRecibida arranca en 0 (no en cantidadPendiente como antes)
        // para forzar acción explícita: el botón "Procesar Recepción" queda
        // disabled hasta que el usuario tilde algún ítem o use "Marcar Todos
        // Completos". Esto cierra el camino del bug 2026-04-26 donde se
        // creaban lotes sin haber recibido mercadería.
        cantidadRecibida: 0,
        cantidadPendiente: Math.max(
          0,
          item.cantidad - (item.cantidadRecibida || 0)
        ),
        precioUnitario: item.precioUnitario,
        estaCompleto: false,
        observaciones: '',
        fechaVencimiento: '',
      }));

      setRecepcionData((prev) => ({
        ...prev,
        items: itemsInicializados,
      }));
    }
  }, [orden]);

  // Actualizar cantidad recibida de un item
  const actualizarCantidadRecibida = (
    detalleId: number,
    nuevaCantidad: number
  ) => {
    setRecepcionData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.detalleId === detalleId
          ? {
              ...item,
              cantidadRecibida: Math.max(
                0,
                Math.min(nuevaCantidad, item.cantidadPendiente)
              ),
              estaCompleto: nuevaCantidad >= item.cantidadPendiente,
            }
          : item
      ),
    }));
  };

  // Vencimiento del lote que generará este ítem. Se guarda tal cual llega del
  // <input type="date"> ('YYYY-MM-DD'); vacío significa lote sin fecha.
  const actualizarFechaVencimiento = (detalleId: number, fecha: string) => {
    setRecepcionData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.detalleId === detalleId
          ? { ...item, fechaVencimiento: fecha }
          : item
      ),
    }));
  };

  // Marcar/desmarcar item como completo
  const toggleItemCompleto = (detalleId: number) => {
    setRecepcionData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.detalleId === detalleId
          ? {
              ...item,
              estaCompleto: !item.estaCompleto,
              cantidadRecibida: !item.estaCompleto ? item.cantidadPendiente : 0,
            }
          : item
      ),
    }));
  };

  // Marcar todos los items como completos
  const marcarTodosCompletos = () => {
    setRecepcionData((prev) => ({
      ...prev,
      items: prev.items.map((item) => ({
        ...item,
        estaCompleto: true,
        cantidadRecibida: item.cantidadPendiente,
      })),
    }));
  };

  // Calcular estadísticas de recepción
  const calcularEstadisticas = () => {
    const totalItems = recepcionData.items.length;
    const itemsCompletos = recepcionData.items.filter(
      (item) => item.cantidadRecibida >= item.cantidadPendiente
    ).length;
    const itemsParciales = recepcionData.items.filter(
      (item) =>
        item.cantidadRecibida > 0 &&
        item.cantidadRecibida < item.cantidadPendiente
    ).length;
    const itemsSinRecibir = recepcionData.items.filter(
      (item) => item.cantidadRecibida === 0
    ).length;

    const valorTotal = recepcionData.items.reduce(
      (sum, item) => sum + item.cantidadPendiente * item.precioUnitario,
      0
    );
    const valorRecibido = recepcionData.items.reduce(
      (sum, item) => sum + item.cantidadRecibida * item.precioUnitario,
      0
    );

    const porcentajeCompletitud =
      totalItems > 0 ? (itemsCompletos / totalItems) * 100 : 0;
    const esRecepcionParcial = itemsParciales > 0 || itemsSinRecibir > 0;

    return {
      totalItems,
      itemsCompletos,
      itemsParciales,
      itemsSinRecibir,
      valorTotal,
      valorRecibido,
      porcentajeCompletitud,
      esRecepcionParcial,
    };
  };

  // Procesar recepción
  const handleProcesarRecepcion = () => {
    const estadisticas = calcularEstadisticas();

    if (estadisticas.esRecepcionParcial) {
      setShowDecisionModal(true);
    } else {
      ejecutarRecepcion('completa');
    }
  };

  // Ejecutar recepción con el tipo especificado
  const ejecutarRecepcion = async (
    tipoRecepcion: RecepcionData['tipoRecepcion']
  ) => {
    setLoading(true);
    try {
      const itemsParaAPI = recepcionData.items
        .filter((item) => item.cantidadRecibida > 0)
        .map((item) => ({
          detalle_id: item.detalleId,
          cantidad_recibida: item.cantidadRecibida,
          observaciones: item.observaciones || undefined,
          // Vacío se manda como undefined para que el backend lo guarde NULL,
          // que es el comportamiento de siempre cuando no hay vencimiento.
          fecha_vencimiento: item.fechaVencimiento || undefined,
        }));

      await recepcionarMutation.mutateAsync({
        id: ordenId,
        data: {
          // Convertir 'YYYY-MM-DD' a ISO con mediodía local para evitar
          // que el cambio de TZ corra el día al UTC midnight cuando se
          // serialize.
          fecha_recepcion: new Date(
            recepcionData.fechaRecepcion + 'T12:00:00'
          ).toISOString(),
          observaciones_recepcion:
            recepcionData.observacionesRecepcion || undefined,
          tipo_recepcion: tipoRecepcion, // Agregar tipo de recepción
          items: itemsParaAPI,
        },
      });

      setShowDecisionModal(false);
      onClose();
    } catch (error) {
      console.error('Error al procesar recepción:', error);
    } finally {
      setLoading(false);
    }
  };

  const estadisticas = calcularEstadisticas();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-3">
            <Package className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {orden?.estado === 'parcial'
                  ? 'Recepcionar Saldo Pendiente'
                  : 'Recepcionar Orden'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {orden?.numero} -{' '}
                {typeof orden?.proveedor === 'string'
                  ? orden.proveedor
                  : orden?.proveedor?.nombre}
              </p>
              {orden?.estado === 'parcial' && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Orden con recepción parcial anterior
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Contenido principal: toma todo el espacio sobrante con flex-1 */}
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Banner de advertencia: la recepción mueve stock real, no es
              una confirmación administrativa de la OC. Recordarlo en rojo. */}
          <div className="flex-shrink-0 mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
            <div className="flex items-start">
              <span className="text-2xl mr-3" aria-hidden>
                ⚠️
              </span>
              <div className="text-sm">
                <p className="font-semibold text-red-900 dark:text-red-200">
                  Confirmación de recepción física
                </p>
                <p className="text-red-800 dark:text-red-300 mt-1">
                  Esta acción registra que la mercadería{' '}
                  <strong>ya llegó físicamente</strong>. Aumentará el stock y
                  creará lotes en inventario. Solo procedé si efectivamente
                  recibiste los productos del proveedor.
                </p>
              </div>
            </div>
          </div>

          {/* Estadísticas */}
          <div className="flex-shrink-0 p-6 bg-gray-50 dark:bg-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {estadisticas.itemsCompletos}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Items Completos
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {estadisticas.itemsParciales}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Items Parciales
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {estadisticas.porcentajeCompletitud.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Completitud
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatearMoneda(estadisticas.valorRecibido)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Valor Recibido
                </div>
              </div>
            </div>
          </div>

          {/* Controles superiores */}
          <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-600">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha de Recepción
                  </label>
                  <input
                    type="date"
                    value={recepcionData.fechaRecepcion}
                    onChange={(e) =>
                      setRecepcionData((prev) => ({
                        ...prev,
                        fechaRecepcion: e.target.value,
                      }))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={marcarTodosCompletos}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                >
                  <Check className="h-4 w-4 inline mr-2" />
                  Marcar Todos Completos
                </button>
              </div>
            </div>
          </div>

          {/* Tabla de items */}
          <div className="flex-1 overflow-auto p-6">
            <div className="space-y-4">
              {recepcionData.items.map((item) => (
                <div
                  key={item.detalleId}
                  className={`p-4 border rounded-lg ${
                    item.cantidadRecibida >= item.cantidadPendiente
                      ? 'border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                      : item.cantidadRecibida > 0
                        ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20'
                        : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800'
                  }`}
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    {/* Bloque info: nombre, precio, cantidades. Se apila en
                        narrow y va horizontal en desktop. min-w-0 permite que
                        el truncate del nombre funcione. */}
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4 min-w-0 flex-1">
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white truncate">
                          {item.ingredienteNombre}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatearMoneda(item.precioUnitario)} por{' '}
                          {item.unidadMedida}
                        </p>
                      </div>
                      <div className="text-left sm:text-right flex-shrink-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Solicitado:{' '}
                          <span className="font-medium">
                            {item.cantidadSolicitada} {item.unidadMedida}
                          </span>
                        </div>
                        {item.cantidadRecibidaAnterior > 0 && (
                          <div className="text-sm text-blue-600">
                            Recibido anterior: {item.cantidadRecibidaAnterior}{' '}
                            {item.unidadMedida}
                          </div>
                        )}
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Pendiente:{' '}
                          <span className="font-medium">
                            {item.cantidadPendiente} {item.unidadMedida}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Controles: Recibir + Completo. flex-shrink-0 para que
                        no se aplasten cuando el modal es estrecho. */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Recibir:
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={item.cantidadPendiente}
                          value={item.cantidadRecibida}
                          onChange={(e) =>
                            actualizarCantidadRecibida(
                              item.detalleId,
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                        <span className="text-sm text-gray-500">
                          {item.unidadMedida}
                        </span>
                      </div>
                      {/* Vencimiento del lote. Opcional, pero determina el
                          orden de consumo: sin fecha el lote va al final de
                          la cola FEFO, detrás de la mercadería vieja. */}
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor={`venc-${item.detalleId}`}
                          className="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Vence:
                        </label>
                        <input
                          id={`venc-${item.detalleId}`}
                          type="date"
                          value={item.fechaVencimiento || ''}
                          onChange={(e) =>
                            actualizarFechaVencimiento(
                              item.detalleId,
                              e.target.value
                            )
                          }
                          title="Fecha de vencimiento del lote. Si se deja vacía, el lote se consumirá al final de la cola."
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={item.estaCompleto}
                          onChange={() => toggleItemCompleto(item.detalleId)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Completo
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Barra de progreso */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                      <span>Progreso</span>
                      <span>
                        {item.cantidadPendiente > 0
                          ? (
                              (item.cantidadRecibida / item.cantidadPendiente) *
                              100
                            ).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          item.cantidadRecibida >= item.cantidadPendiente
                            ? 'bg-green-600'
                            : item.cantidadRecibida > 0
                              ? 'bg-yellow-600'
                              : 'bg-gray-400'
                        }`}
                        style={{
                          width: `${
                            item.cantidadPendiente > 0
                              ? Math.min(
                                  (item.cantidadRecibida /
                                    item.cantidadPendiente) *
                                    100,
                                  100
                                )
                              : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Observaciones */}
          <div className="flex-shrink-0 p-6 border-t border-gray-200 dark:border-gray-600">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observaciones de Recepción
            </label>
            <textarea
              value={recepcionData.observacionesRecepcion}
              onChange={(e) =>
                setRecepcionData((prev) => ({
                  ...prev,
                  observacionesRecepcion: e.target.value,
                }))
              }
              placeholder="Comentarios sobre la recepción..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Botones de acción */}
          <div className="flex-shrink-0 flex items-center justify-between p-6 bg-gray-50 dark:bg-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {estadisticas.esRecepcionParcial && (
                <div className="flex items-center text-yellow-600">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Recepción parcial detectada
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancelar
              </button>
              <button
                onClick={handleProcesarRecepcion}
                disabled={
                  loading ||
                  recepcionData.items.every(
                    (item) => item.cantidadRecibida === 0
                  )
                }
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {loading ? 'Procesando...' : 'Confirmar recepción física'}
              </button>
            </div>
          </div>
        </div>

        {/* Modal de decisión para recepción parcial */}
        {showDecisionModal && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Entrega Parcial Detectada
                  </h3>
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Algunos items no fueron recibidos completamente. ¿Qué deseas
                  hacer?
                </p>

                <div className="space-y-3">
                  <button
                    onClick={() => ejecutarRecepcion('parcial-mantener')}
                    className="w-full p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-blue-600 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          Mantener OC Abierta
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Registrar recepción parcial y mantener la orden activa
                          para futuras entregas
                        </div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => ejecutarRecepcion('parcial-nueva-oc')}
                    className="w-full p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center">
                      <Plus className="h-5 w-5 text-green-600 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          Crear Nueva OC
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Cerrar esta OC y crear automáticamente una nueva con
                          el saldo pendiente
                        </div>
                      </div>
                    </div>
                  </button>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDecisionModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecepcionOrdenCompra;
